import { useState } from 'react';

import { Loader2, Send } from 'lucide-react';

import { DocumentChecklist } from '@/modulos/operacao/comum/DocumentChecklist';
import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';

import { useDocumentosDoCredenciado } from './detalheDados';

interface AbaDocumentosProps {
  credenciadoId: string;
  nomeDoCredenciado: string;
}

/**
 * Aba "Documentos". Usa `DocumentChecklist` compartilhado (ja com "Enviar"
 * ligado ao aviso de indisponibilidade) e acrescenta so o "Solicitar
 * Documento", que no protótipo (`SolicitarDocumentoModal`) enviava uma
 * notificacao real ao usuario do credenciado — sem modulo de notificacao na
 * API, o envio vira o mesmo aviso honesto.
 */
export function AbaDocumentos({ credenciadoId, nomeDoCredenciado }: AbaDocumentosProps) {
  const { data: itens, isLoading } = useDocumentosDoCredenciado(credenciadoId);
  const [dialogAberto, setDialogAberto] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setDialogAberto(true)}>
          <Send className="mr-2 h-4 w-4" aria-hidden="true" /> Solicitar Documento
        </Button>
      </div>

      {isLoading || !itens ? (
        <Spinner size="sm" />
      ) : (
        <DocumentChecklist itens={itens} title="Documentos do credenciado" />
      )}

      <SolicitarDocumentoDialog
        open={dialogAberto}
        onOpenChange={setDialogAberto}
        nomeDoCredenciado={nomeDoCredenciado}
      />
    </div>
  );
}

const DOCUMENTOS_SOLICITAVEIS = [
  'CND Municipal',
  'CND Federal',
  'CND Estadual',
  'CNDT',
  'CRF FGTS',
  'CADIN Anexo III',
];

function SolicitarDocumentoDialog({
  open,
  onOpenChange,
  nomeDoCredenciado,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nomeDoCredenciado: string;
}) {
  const [documento, setDocumento] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = () => {
    setEnviando(true);
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
    setEnviando(false);
    onOpenChange(false);
    setDocumento('');
    setMensagem('');
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Solicitar documento"
      description={`Envia um pedido de documento para ${nomeDoCredenciado}.`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!documento || enviando}>
            {enviando ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Enviar solicitação
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Documento</Label>
          <Select value={documento} onValueChange={setDocumento}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder="Selecione o documento" />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENTOS_SOLICITAVEIS.map((nome) => (
                <SelectItem key={nome} value={nome}>
                  {nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Observação (opcional)</Label>
          <Textarea
            className="mt-1.5"
            value={mensagem}
            onChange={(evento) => setMensagem(evento.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Informe detalhes sobre o que precisa ser enviado ou corrigido…"
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
